import { useState } from 'react';
import { useLoggingQuery } from 'lib/apollo/apollo-hooks';
import { get } from 'lib/network/http';
import { GET_UPLOAD_FORM_DATA } from './file-dropper.graphql';
import {
  UPLOAD_ERROR,
  ENTITY_TOO_LARGE,
  TOO_LARGE_ERROR,
} from './file-dropper.constants';
import type {
  GetUploadFormDataQuery,
  GetUploadFormDataQueryVariables,
} from './file-dropper.graphql.generated';
import { useFileDropperContext } from './file-dropper.context';

function parseError(errorText: string) {
  const body = errorText;
  const parser = new DOMParser();
  const doc = parser.parseFromString(body, 'text/xml');
  if (doc && doc.childNodes && doc.childNodes[0]) {
    const nodes = Array.from(doc.childNodes[0].childNodes);
    const codeNode = nodes.find(child => child.nodeName === 'Code');

    const maxSizeAllowedNode = nodes.find(
      child => child.nodeName === 'MaxSizeAllowed'
    );

    if (
      maxSizeAllowedNode?.textContent &&
      codeNode?.textContent === ENTITY_TOO_LARGE
    ) {
      const maxSize = parseInt(maxSizeAllowedNode.textContent, 10);
      return `${TOO_LARGE_ERROR} ${Math.floor(maxSize / 1048576)}MB`;
    }
  }
  return UPLOAD_ERROR;
}

export const useFileUploadProgress = (name: string) => {
  const { setXhrObject, setError, setIsUploading } =
    useFileDropperContext(name);

  const [progress, setProgress] = useState<ProgressEvent | undefined>(
    undefined
  );

  const { refetch: refetchUploadUrl } = useLoggingQuery<
    GetUploadFormDataQuery,
    GetUploadFormDataQueryVariables
  >(GET_UPLOAD_FORM_DATA, {
    fetchPolicy: 'no-cache',
    skip: true,
  });

  const uploadToS3 = (file: File, uploadFormData: GetUploadFormDataQuery) => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      const fields = uploadFormData.getUploadFormData.fields;
      for (const key in fields) {
        formData.append(key, fields[key]);
      }
      formData.append('file', file);

      const xhr = new XMLHttpRequest();
      setXhrObject(xhr);
      xhr.upload.onprogress = event => {
        setProgress(event);
      };

      xhr.onreadystatechange = (ev: Event) => {
        if (xhr.readyState !== 4) {
          return;
        }

        if (xhr.status === 0) {
          //abort
          setXhrObject(undefined);
          setProgress(undefined);
        }

        if (xhr.status === 400 && ev) {
          setXhrObject(undefined);
          setProgress(undefined);
          reject(parseError((ev.currentTarget as XMLHttpRequest).responseText));
        }

        if (xhr.status === 200 || xhr.status === 204) {
          resolve({ status: 200, text: 'ok' });
          setXhrObject(undefined);
        }
      };

      xhr.open('POST', uploadFormData.getUploadFormData.url, true);
      xhr.send(formData);
    });
  };

  const upload = async (file: File) => {
    setError(undefined);
    setIsUploading(true);

    const result = await refetchUploadUrl({
      input: { fileName: file.name },
    }).catch(e => {
      setError(UPLOAD_ERROR);
      throw new Error(e);
    });

    if (
      result.data.getUploadFormData.url &&
      result.data.getUploadFormData.fields
    ) {
      await uploadToS3(file, result.data)
        .catch(e => {
          setError(e);
          throw new Error(e);
        })
        .finally(() => {
          setIsUploading(false);
        });
      return result.data.getUploadFormData.fields.key;
    }
  };

  return {
    upload,
    progress,
  };
};

export type FileUploadResult = {
  key: string;
  fileName: string;
};

export const useGetFileAndUpload = (fieldName: string) => {
  const [loading, setLoading] = useState<boolean>(false);

  const { file, error, setFile, setError } = useFileDropperContext(fieldName);
  const { upload, progress } = useFileUploadProgress(fieldName);

  const getFileAndUpload = async ({
    fileUrl,
    fileName,
  }: {
    fileUrl: string;
    fileName: string;
  }) => {
    return new Promise<FileUploadResult>(async (resolve, reject) => {
      setLoading(true);
      const result = await get({ path: fileUrl }, 'file-upload.tsx');
      if (result.ok) {
        const blob = await result.blob();
        const file = new File([blob], fileName, blob);
        setFile(file);
        upload(file)
          .then((s3Key: string) => {
            setLoading(false);
            resolve({ key: s3Key, fileName: file.name });
          })
          .catch(() => {
            setLoading(false);
          });
      } else {
        setError('An error occurred saving the file. Please try again later.');
        setLoading(false);
        reject();
      }
    });
  };

  return {
    getFileAndUpload,
    progress,
    loading,
    error,
    file,
  };
};
